import { hasPermission, PermissionsType } from '@eppendorf/vnls-user-tenant-utils';
import { QueryClient } from '@tanstack/react-query';
import { ReactElement, useEffect } from 'react';
import {
  createBrowserRouter,
  LoaderFunctionArgs,
  Navigate,
  redirect,
} from 'react-router-dom';

import { AuthGuard } from '$shared/auth/auth-guard';
import { getIdTokenPayload, NoSessionError } from '$shared/auth/auth-service';
import {
  clearPostLoginRedirectPath,
  peekPostLoginRedirectPath,
} from '$shared/auth/post-login-redirect.utils';
import { useTokenAttributes } from '$shared/custom-hooks/useTokenAttributes';

import { appConfigLoader } from '$components/app-config/app-config-loader';
import { CookieList } from '$components/cookie-policy';
import { CookieElementWrapper } from '$components/cookie-policy/cookie-element-wrapper';
import { Loader } from '$components/loader/loader';
import { IncidentsList } from '$features/incidents-list/incidents-list';
import { NotificationRuleList } from '$features/notification-rule-list';
import {
  auditEventsLoader,
  AuditEventsPage,
} from '$pages/audit-events/audit-events-page';
import { DashboardPage } from '$pages/dashboard';
import { DevicesPage, devicesLoader } from '$pages/devices/devices-page';
import { ErrorPage } from '$pages/error-page';
import { SetupAccountPage } from '$pages/landing/setup-account/setup-account';
import { SigninAccountPage } from '$pages/landing/signin-account/signin-account';
import { monitoringLoader, MonitoringPage } from '$pages/monitoring/monitoring-page';
import { Notifications, notificationsLoader } from '$pages/notifications/notifications';
import {
  librariesLoader,
  OpenSourceLibrariesPage,
} from '$pages/open-source-libraries/open-source-libraries-page';
import {
  organizationSettingsLoader,
  OrganizationSettingsPage,
} from '$pages/organization-settings/organization-settings-page';
import { PlaygroundPage } from '$pages/playground/playground-page';
import { ReportHubPage, reportHubLoader } from '$pages/report-hub/report-hub-page';
import { profileLoader, RootLayout } from '$pages/root-layout';
import { SignoutPage } from '$pages/signout/signout-page';
import {
  UserManagementPage,
  usersLoader,
} from '$pages/user-management/user-management-page';

async function rootLoader(qc: QueryClient): Promise<null> {
  return Promise.all([
    profileLoader(qc),
    getIdTokenPayload().then((claims) =>
      hasPermission('VIEW_DEVICE_OVERVIEW', claims.permissions)
        ? appConfigLoader(qc)
        : null,
    ),
  ])
    .then(() => null)
    .catch((error) => {
      if (error instanceof NoSessionError) {
        // In case user is not logged in yet, we dont want to see the errorElement of the root route.
        // Which means, we dont want to throw/reject here.
        return Promise.resolve(null);
      }

      throw new Error(error);
    });
}

// Reads the saved deep-link path (pure read, safe during render) and clears it in an
// effect afterwards, so this stays correct even under React 18 StrictMode's dev-only
// double-invocation of render-phase code.
function DefaultPageRedirect(): ReactElement {
  const { permissions } = useTokenAttributes(['permissions']);
  const fallbackPath = hasPermission('VIEW_DEVICE_OVERVIEW', permissions)
    ? '/devices'
    : '/user-management';
  const target = peekPostLoginRedirectPath() ?? fallbackPath;

  useEffect(() => {
    if (permissions) clearPostLoginRedirectPath();
  }, [permissions]);

  return permissions ? <Navigate to={target} replace /> : <Loader />;
}

async function requirePermission(permission: PermissionsType): Promise<null> {
  const claims = await getIdTokenPayload();
  if (!hasPermission(permission, claims.permissions)) {
    throw redirect('/user-management');
  }
  return null;
}

export function createRouter(queryClient: QueryClient, isAuthenticated: boolean) {
  const navigateToDefaultPage = () => <DefaultPageRedirect />;

  return createBrowserRouter([
    {
      path: '/',
      loader: isAuthenticated ? () => rootLoader(queryClient) : undefined,
      element: (
        <AuthGuard fallback={<Navigate to="/signin" />}>
          <RootLayout />
        </AuthGuard>
      ),
      errorElement: <ErrorPage />,
      children: [
        {
          path: '',
          element: navigateToDefaultPage(),
        },
        {
          path: 'dashboard',
          permission: 'VIEW_DEVICE_OVERVIEW',
          element: <DashboardPage />,
          errorElement: <DashboardPage />,
        },
        {
          path: 'devices',
          permission: 'VIEW_DEVICE_OVERVIEW',
          element: <DevicesPage />,
          errorElement: <DevicesPage />,
          loader: () => devicesLoader(queryClient),
        },
        {
          path: 'monitoring',
          permission: 'VIEW_DEVICE_OVERVIEW',
          element: <MonitoringPage />,
          errorElement: <MonitoringPage />,
          loader: () => monitoringLoader(queryClient),
        },
        {
          path: 'notifications',
          permission: 'VIEW_OVERVIEW',
          element: <Notifications />,
          errorElement: <Notifications />,
          loader: () => notificationsLoader(queryClient),
          children: [
            {
              path: 'notifications-rules',
              element: <NotificationRuleList />,
              errorElement: <NotificationRuleList />,
            },
            {
              path: 'incidents',
              element: <IncidentsList />,
              errorElement: <IncidentsList />,
            },
            {
              path: '',
              element: <Navigate to="incidents" replace />,
              errorElement: <Navigate to="incidents" replace />,
            },
          ],
        },
        {
          path: 'user-management',
          permission: 'VIEW_USER_OVERVIEW',
          element: <UserManagementPage />,
          errorElement: <UserManagementPage />,
          loader: () => usersLoader(queryClient),
        },
        {
          path: 'settings',
          permission: 'VIEW_ORGANIZATION_SETTINGS',
          element: <OrganizationSettingsPage />,
          errorElement: <OrganizationSettingsPage />,
          loader: () => organizationSettingsLoader(queryClient),
        },
        {
          path: 'audit-events',
          permission: 'VIEW_AUDIT_TRAIL_OVERVIEW',
          element: <AuditEventsPage />,
          errorElement: <AuditEventsPage />,
          loader: (args: LoaderFunctionArgs) => auditEventsLoader(queryClient, args),
        },
        {
          path: 'report-hub',
          permission: 'READ_STABILITY_REPORT',
          loader: () => reportHubLoader(queryClient),
          children: [
            {
              path: ':tab',
              element: <ReportHubPage />,
              errorElement: <ReportHubPage />,
            },
            {
              path: '',
              element: <Navigate to="stability" replace />,
            },
          ],
        },
        {
          path: 'playground',
          permission: 'VIEW_DEVICE_OVERVIEW',
          element: <PlaygroundPage />,
          errorElement: <PlaygroundPage />,
        },
        {
          path: 'cookie-list',
          element: (
            <CookieElementWrapper>
              <CookieList />
            </CookieElementWrapper>
          ),
        },
      ].map(({ permission, ...route }) => ({
        ...route,
        loader:
          isAuthenticated && permission
            ? async (args: LoaderFunctionArgs) => {
                await requirePermission(permission as PermissionsType);
                return (await route.loader?.(args)) ?? null;
              }
            : (isAuthenticated && route.loader) || undefined,
      })),
    },
    {
      path: 'signin',
      element: isAuthenticated ? navigateToDefaultPage() : <SigninAccountPage />,
    },
    {
      path: 'setup',
      element: isAuthenticated ? navigateToDefaultPage() : <SetupAccountPage />,
    },
    {
      path: 'oslibraries',
      element: <OpenSourceLibrariesPage />,
      loader: () => librariesLoader(),
    },
    {
      path: 'logout',
      element: <SignoutPage />,
    },
  ]);
}
